import React, { useMemo, useState } from "react";
import { FaUser } from "react-icons/fa";
import { InputField } from "../../../../../components/reactHookFormFields/inputField";
import { CheckBox } from "../../../../../components/reactHookFormFields/checkbox";
import { useForm } from "react-hook-form";
import { useGroupChatContext } from "../../../../../context/groupchatContext";
import MainPageDialog from "../../../../../components/dialog";
import ChangePincode from "../../../changePincode";
import ChangePassword from "../../../changePassword";
import TwoFa from "../../../twoFASetting";
import { FaKey } from "react-icons/fa";
import { IoIosKeypad } from "react-icons/io";
import { MdOutlineSecurity } from "react-icons/md";
import { useUpdateNurseInfoMutation } from "../../../../../services/nurseService";
import { toast } from "react-toastify";
import { MaskField } from "../../../../../components/reactHookFormFields/maskField";
import { formatPhoneNumber } from "../../../../../utils/common_lib";

function UserProfile() {
  const groupChatContext = useGroupChatContext();
  const { authData, setAuthData, isShowPinCreateScreenOnFirstLogin } = useMemo(() => ({
    isShowPinCreateScreenOnFirstLogin: groupChatContext.isShowPinCreateScreenOnFirstLogin,
    authData: groupChatContext.authData,
    setAuthData: groupChatContext.setAuthData,
  }));

  if(!authData?.username) {
    toast.error("Error loading user information!");
  }

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: authData?.name || "",
      email: authData?.email || "",
      username: authData?.username || "",
      phone: formatPhoneNumber(authData?.phone || "") || "",
      notify_on_sms: authData?.notify_on_sms == 1,
      notify_on_sms_urgent: authData?.notify_on_sms_urgent == 1,
    },
  });

  const title = isShowPinCreateScreenOnFirstLogin ? "Create New PIN" : "Change PIN";
  const [isOpenChangePinCodePopup, setIsOpenChangePinCodePopup] = useState(JSON.parse(localStorage.getItem("createPinOnFirstLogin")) == null ? false : localStorage.getItem("createPinOnFirstLogin"));
  const [isOpenChangePasswordPopup, setIsOpenChangePasswordPopup] = useState(false);
  const [isOpen2FaPopup, setIsOpen2FaPopup] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveNurseProfileTrigger] = useUpdateNurseInfoMutation();

  const menuItems = useMemo(() => [
      {
        label: "Change Password",
        onClick: () => setIsOpenChangePasswordPopup(true),
        icon: <FaKey />,
      },
      {
        label: "Change Pin",
        onClick: () => setIsOpenChangePinCodePopup(true),
        icon: <IoIosKeypad />,
      },
      {
        label: "Security",
        onClick: () => setIsOpen2FaPopup(true),
        icon: <MdOutlineSecurity />,
      },
    ], []);

  const onSubmit = async (data) => {
    if(!data?.email) {
      toast.error("Email is required");
      return;
    }
    try {
      setIsSaving(true);
      saveNurseProfileTrigger({
        ...data,
        id: authData.id,
      })
        .then((response) => {
          const { code, msg } = response?.data.response || {};
          if (code === "0" || code === 0) {
            toast.success(msg);
            const authDataLocal = JSON.parse(localStorage.getItem("auth"));
            authDataLocal["name"] = data.name;
            authDataLocal["email"] = data.email;
            authDataLocal["phone"] = data.phone;
            authDataLocal["notify_on_sms"] = !!data.notify_on_sms;
            authDataLocal["notify_on_sms_urgent"] = !!data.notify_on_sms_urgent;
            localStorage.setItem("auth", JSON.stringify(authDataLocal));
            setAuthData(authDataLocal);
          }
          else if (code === "-1" || code === -1) {
            // toast.error(msg);
          } else {
            toast.error("Unable to update nurse info");
          }
        })
        .catch((error) => {
          console.error("Error while updating nurse info:", error);
          toast.error("Error while updating nurse info");
        })
        .finally(() => {
          setIsSaving(false);
        });
    } catch (error) {
      console.error("Error updating nurse info:", error);
      toast.error("Error updating nurse info");
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full min-h-full flex flex-col bg-gray-50/50">
      {/* Header */}
      <div className="flex w-full items-center justify-between px-4 sm:px-6 shadow-sm h-14 bg-white border-b border-gray-100 shrink-0">
        <p className="text-base sm:text-lg font-semibold text-gray-800">User Profile</p>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 w-full overflow-y-auto overflow-x-hidden">
        <div className="w-full max-w-6xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
          <div className="flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-8">
            {/* Dialogs */}
            {isOpenChangePinCodePopup ? (
              <MainPageDialog
                title={title}
                isShowCloseBtn={!isShowPinCreateScreenOnFirstLogin}
                onCancelHandler={() => setIsOpenChangePinCodePopup(false)}
                modalSize="md"
                customHeight={"max-h-[40vh]"}
                component={
                  <ChangePincode
                    closeChangePinCodePopup={() =>
                      setIsOpenChangePinCodePopup(false)
                    }
                  />
                }
                footer={false}
              />
            ) : null}

            {isOpenChangePasswordPopup ? (
              <MainPageDialog
                title="Change Password"
                onCancelHandler={() => setIsOpenChangePasswordPopup(false)}
                modalSize="md"
                customHeight={"max-h-[60vh]"}
                component={
                  <ChangePassword
                    closeChangePasswordPopup={() =>
                      setIsOpenChangePasswordPopup(false)
                    }
                  />
                }
                footer={false}
              />
            ) : null}

            {isOpen2FaPopup ? (
              <MainPageDialog
                title="Security"
                onCancelHandler={() => setIsOpen2FaPopup(false)}
                modalSize="md"
                customHeight={"max-h-[80vh]"}
                component={<TwoFa close2FaPopup={() => setIsOpen2FaPopup(false)} />}
                footer={false}
              />
            ) : null}

            {/* Avatar - full width on mobile, fixed on desktop */}
            <div className="flex justify-center lg:justify-start lg:shrink-0">
              <div className="flex items-center justify-center h-24 w-24 sm:h-28 sm:w-28 lg:h-32 lg:w-32 rounded-full border-2 border-violet-200 bg-gradient-to-br from-violet-50 to-white text-violet-400 shadow-inner transition-shadow hover:shadow-md">
                <FaUser className="h-10 w-10 sm:h-12 sm:w-12 lg:h-14 lg:w-14" />
              </div>
            </div>

            {/* Profile form card */}
            <form
              className="flex-1 min-w-0 flex flex-col p-4 sm:p-6 gap-5 sm:gap-6 bg-white rounded-xl shadow-sm border border-gray-100 lg:border-r lg:border-r-gray-200 lg:rounded-r-none lg:rounded-l-xl"
              onSubmit={handleSubmit(onSubmit)}
            >
              <p className="text-base sm:text-lg font-semibold text-gray-800">Profile Settings</p>
              <InputField
                control={control}
                name="name"
                title="Full Name"
                type="text"
                autoComplete="off"
                errors={errors}
              />
              <InputField
                control={control}
                name="email"
                title="Email"
                type="email"
                autoComplete="off"
                errors={errors}
                rules={{
                  required: {
                    value: true,
                    message: "Email is required",
                  },
                  validate: (value) => {
                    if (!value) {
                      return true;
                    }
                    try {
                      return (
                        [/^[^\s@]+@[^\s@]+\.[^\s@]+$/].every((pattern) =>
                          pattern.test(value)
                        ) || "Invalid email address"
                      );
                    } catch (error) {
                      console.error("Error validating email:", error);
                      return "Invalid email address";
                    }
                  },
                }}
              />
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-4">
                <InputField
                  control={control}
                  name="username"
                  title="Username"
                  isDisabled={true}
                  type="text"
                  autoComplete="off"
                  errors={errors}
                />
                <MaskField
                  mask="(999) 999-9999"
                  title="Phone"
                  name="phone"
                  control={control}
                  rules={{
                    pattern: {
                      value: /^^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
                      message: "Please Enter Correct Number",
                    },
                  }}
                  errors={errors}
                />
              </div>
              <div className="w-full border-t border-gray-100 pt-4">
                <div
                  className="relative min-w-0 w-full rounded-lg border border-gray-200 bg-gray-50/80 px-4 pb-4 pt-5 shadow-sm"
                  role="group"
                  aria-labelledby="user-profile-sms-heading"
                >
                  <p
                    id="user-profile-sms-heading"
                    className="absolute left-4 top-0 z-[1] -translate-y-1/2 bg-white px-2 text-sm text-gray-800"
                  >
                    SMS notifications
                  </p>
                  <div className="flex w-full flex-col gap-4 sm:flex-row sm:gap-4">
                    <div className="flex w-full min-w-0 items-center min-h-[35px]">
                      <CheckBox
                        control={control}
                        name="notify_on_sms"
                        title="Notify on All SMS"
                        errors={errors}
                        containterCls="w-full"
                      />
                    </div>
                    <div className="flex w-full min-w-0 items-center min-h-[35px]">
                      <CheckBox
                        control={control}
                        name="notify_on_sms_urgent"
                        title="Notify on SMS for only urgent messages"
                        errors={errors}
                        containterCls="w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-start">
                <button
                  className={`flex items-center justify-center font-medium rounded-lg text-sm sm:text-[14px] text-white border border-violet-600 bg-bg-primary hover:bg-violet-700 active:scale-[0.98] transition-all duration-200 px-4 py-2.5 min-w-[100px] ${isSaving ? "opacity-50 cursor-not-allowed" : ""}`}
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>

            {/* Security card */}
            <div className="flex flex-col p-4 sm:p-6 gap-4 bg-white rounded-xl shadow-sm border border-gray-100 lg:border-l-0 lg:rounded-l-none lg:rounded-r-xl min-w-0">
              <p className="text-base sm:text-lg font-semibold text-gray-800">Security Settings</p>
              <div className="flex flex-col gap-3">
                {menuItems.map((item, index) => (
                  <button
                    key={index}
                    type="button"
                    className="flex items-center gap-3 px-4 py-3 font-medium rounded-lg text-sm sm:text-[14px] text-white border border-violet-600 bg-bg-primary hover:bg-violet-700 active:scale-[0.99] transition-all duration-200 text-left"
                    onClick={item.onClick}
                  >
                    <span className="shrink-0 text-lg opacity-90">{item.icon}</span>
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserProfile;
